import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import {
  Button,
  ConfigProvider,
  Empty,
  Select,
  Skeleton,
  Space,
  Spin,
  message,
} from "antd";
import {
  ApartmentOutlined,
  FileExcelOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { updateMenuData } from "../../../../store/slices/misc/menuData";
import TopMenu from "../../../../route/topMenu";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import { ThreeMPhaseNode } from "../ThreeMConsolidation/threeMShared";
import ManpowerKpiCards from "./ManpowerKpiCards";
import ManpowerCostCharts from "./ManpowerCostCharts";
import ManpowerSummaryTable from "./ManpowerSummaryTable";
import PhaseCostDrawer from "./PhaseCostDrawer";
import ManpowerTaskBreakdownDrawer from "./ManpowerTaskBreakdownDrawer";
import ManpowerMonthlyScheduleDrawer from "./ManpowerMonthlyScheduleDrawer";
import { ManpowerSummaryData, ManpowerSummaryRow, num } from "./manpowerShared";
import WbsCodeToggle from "../../shared/WbsCodeToggle";
import { useWbsName } from "../../shared/useWbsCodes";

// Project-level manpower cost analysis: labour items aggregated across the
// two manpower sources — applied PRW norms ('PRW') and task resources
// ('Others') — KPI cards + PRW/Others donut + Pareto + unified register.
// Project context comes the same way as MaterialCostAnalysis:
// location.state { id, projectName } with the optional :projectId fallback.

const ManpowerCostAnalysisReport = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const { projectName: stateProjectName, id: stateProjectId } =
    (location.state as any) || {};
  const projectId = stateProjectId ?? params.projectId;

  const [data, setData] = useState<ManpowerSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [phaseId, setPhaseId] = useState<number | undefined>(undefined);
  const [activityId, setActivityId] = useState<number | undefined>(undefined);

  // Phase/Activity dropdown options come from the unfiltered 3M consolidation
  // tree (same source the 3M report uses), so the manpower summary payload —
  // which carries no phase/activity info — never has to.
  const [basePhases, setBasePhases] = useState<ThreeMPhaseNode[]>([]);

  const [refreshTick, setRefreshTick] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [phaseDrawerOpen, setPhaseDrawerOpen] = useState(false);

  // Org branding for the Excel export. The currentUser slice stores the raw
  // /users/currentUser payload; orgName/orgLogo exist at runtime even though
  // the slice's local Data interface doesn't declare them.
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgName: string | undefined = currentUser?.orgName;
  const orgLogoUrl: string | undefined = currentUser?.orgLogo;

  // Export toasts go through a message instance whose holder lives inside the
  // report container — global message portals to document.body, which is
  // invisible while this container is fullscreen.
  const [msgApi, msgHolder] = message.useMessage();
  // Per-item task drill-down (phase → activity → task); null = closed.
  // Per-item month-by-month schedule; mutually exclusive with the task
  // drill-down (antd stacks drawers at one z-index).
  const [scheduleRow, setScheduleRow] = useState<ManpowerSummaryRow | null>(
    null,
  );
  const [breakdownRow, setBreakdownRow] = useState<ManpowerSummaryRow | null>(
    null
  );

  // Fullscreen: the report container goes fullscreen via the browser API.
  // Popups (Select dropdowns, tooltips) and the drawers are re-targeted INTO
  // the container — portals left on document.body would be invisible while
  // another element is fullscreen. Same pattern as ThreeMConsolidation.
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    // Covers both the button and ESC (the browser exits without telling us).
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.().catch(() => {
        message.warning("Fullscreen is not supported in this browser");
      });
    }
  };

  const popupContainer = () => containerRef.current || document.body;

  // Project top-menu, same as WBSViewNew / ThreeMConsolidation
  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Filter options: one unfiltered consolidation load per project.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    threeMReportApis
      .get3MConsolidation(projectId)
      .then((response) => {
        if (!cancelled && response?.status) {
          setBasePhases(response?.data?.phases || []);
        }
      })
      .catch((err) => {
        // Non-fatal: the report still renders, only the filters stay empty.
        console.error("Error loading phase/activity filter options:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setError("Project ID not found");
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await threeMReportApis.get3MManpowerSummary(
          projectId,
          { phaseId, activityId }
        );
        const { status, data: payload } = response || {};
        if (cancelled) return;
        if (status) {
          setData(payload || {});
        } else {
          setError(response?.message || "Failed to load manpower summary");
          message.error(response?.message || "Failed to load manpower summary");
        }
      } catch (err) {
        console.error("Error loading manpower cost analysis:", err);
        if (!cancelled) {
          setError("Error loading Manpower Cost Analysis");
          message.error("Error loading Manpower Cost Analysis");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, phaseId, activityId, refreshTick]);

  const projectName = data?.projectName || stateProjectName || "Project";
  const items = useMemo(() => data?.items || [], [data]);

  // Grand total for shares: trust the API value, fall back to the row sum
  // (gRPC may omit zero/absent fields).
  const grandTotal = useMemo(() => {
    const apiTotal = num(data?.totalCost);
    if (apiTotal > 0) return apiTotal;
    return items.reduce((sum, m) => sum + num(m.totalCost), 0);
  }, [data, items]);

  const prwCost = useMemo(() => {
    const apiPrw = num(data?.prwCost);
    if (apiPrw > 0) return apiPrw;
    return items
      .filter((m) => m.source === "PRW")
      .reduce((sum, m) => sum + num(m.totalCost), 0);
  }, [data, items]);

  const othersCost = useMemo(() => {
    const apiOthers = num(data?.othersCost);
    if (apiOthers > 0) return apiOthers;
    return items
      .filter((m) => m.source !== "PRW")
      .reduce((sum, m) => sum + num(m.totalCost), 0);
  }, [data, items]);

  const wbsName = useWbsName();
  const phaseOptions = useMemo(
    () =>
      basePhases.map((phase) => ({
        label: wbsName(phase.phaseName),
        value: phase.phaseId,
      })),
    [basePhases]
  );

  const activityOptions = useMemo(() => {
    if (!phaseId) return [];
    const phase = basePhases.find((p) => p.phaseId === phaseId);
    return (phase?.activities || []).map((activity) => ({
      label: wbsName(activity.activityName),
      value: activity.activityId,
    }));
  }, [basePhases, phaseId, wbsName]);

  const hasFilters = !!phaseId || !!activityId;

  const clearFilters = () => {
    setPhaseId(undefined);
    setActivityId(undefined);
  };

  // Styled multi-sheet Excel export: fetches the all-items task breakdown for
  // the current filters, then hands everything to the lazily-loaded exceljs
  // builder. The logo fetch is best-effort (cross-origin blob).
  const handleExport = async () => {
    if (!projectId || !data) return;
    setExporting(true);
    try {
      const [breakdownResponse, monthResponse, helpers] = await Promise.all([
        threeMReportApis.get3MManpowerTaskBreakdownAll(projectId, {
          phaseId,
          activityId,
        }),
        threeMReportApis.get3MManpowerMonthlyScheduleAll(projectId, {
          phaseId,
          activityId,
        }),
        import("../shared/excelHelpers"),
      ]);
      // The month grid is additive: a project with no task schedules still
      // gets the other sheets rather than a failed export.
      if (!monthResponse?.status) {
        console.warn(
          "Month-wise sheet skipped:",
          monthResponse?.message || "monthly schedule fetch failed"
        );
      }
      if (!breakdownResponse?.status) {
        msgApi.error(breakdownResponse?.message || "Failed to load export data");
        return;
      }
      const logo = await helpers.fetchLogoBase64(orgLogoUrl);
      const { default: exportWorkbook } = await import("./exportWorkbook");
      const phaseName = phaseId
        ? basePhases.find((p) => p.phaseId === phaseId)?.phaseName
        : undefined;
      const activityName =
        phaseId && activityId
          ? basePhases
              .find((p) => p.phaseId === phaseId)
              ?.activities?.find((a) => a.activityId === activityId)
              ?.activityName
          : undefined;
      await exportWorkbook({
        data: { ...data, projectName },
        grandTotal,
        prwCost,
        othersCost,
        breakdownRows: breakdownResponse?.data?.rows || [],
        monthCells: monthResponse?.status ? monthResponse.data?.rows || [] : [],
        orgName,
        logo,
        filters: { phaseName, activityName },
      });
      msgApi.success("Excel exported");
    } catch (err) {
      console.error("Error exporting Manpower Cost Analysis:", err);
      msgApi.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Head title="Manpower Cost Analysis" />
      <Content>
        <div
          ref={containerRef}
          style={{
            position: "relative",
            ...(isFullscreen
              ? { background: "#f5f6fa", overflowY: "auto", padding: 16 }
              : {}),
          }}
        >
        <ConfigProvider getPopupContainer={popupContainer}>
        {msgHolder}
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
          <div className="card-inner card-inner-lg">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
                  Manpower Cost Analysis
                </h5>
                <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                  {projectName} · Labour cost rollup across PRW norms and task
                  resources, grouped by BOQ norm group
                </span>
              </div>
              <Space>
                <Button
                  icon={
                    isFullscreen ? (
                      <FullscreenExitOutlined />
                    ) : (
                      <FullscreenOutlined />
                    )
                  }
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </Button>
                <Button
                  icon={<ApartmentOutlined />}
                  onClick={() => setPhaseDrawerOpen(true)}
                  disabled={basePhases.length === 0}
                >
                  Phase Costs
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exporting}
                  disabled={loading || !data || items.length === 0}
                  onClick={handleExport}
                >
                  Export
                </Button>
                <WbsCodeToggle />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshTick((t) => t + 1)}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {/* Filter bar */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Phase"
                style={{ width: 220 }}
                options={phaseOptions}
                value={phaseId}
                onChange={(value) => {
                  setPhaseId(value);
                  setActivityId(undefined);
                }}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Activity"
                style={{ width: 220 }}
                options={activityOptions}
                value={activityId}
                disabled={!phaseId}
                onChange={(value) => setActivityId(value)}
              />
              {hasFilters ? (
                <Button type="link" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : null}
            </div>

            {loading && !data ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : error && !data ? (
              <Empty description={error} style={{ padding: "60px 0" }} />
            ) : (
              <Spin spinning={loading}>
                <ManpowerKpiCards data={data} grandTotal={grandTotal} />
                {items.length === 0 ? (
                  <Empty
                    description={
                      hasFilters
                        ? "No labour items match the current filters"
                        : "No manpower cost data for this project yet"
                    }
                    style={{ padding: "40px 0" }}
                  />
                ) : (
                  <>
                    <ManpowerCostCharts
                      items={items}
                      grandTotal={grandTotal}
                      prwCost={prwCost}
                      othersCost={othersCost}
                    />
                    <ManpowerSummaryTable
                      items={items}
                      grandTotal={grandTotal}
                      onTasksClick={(row) => {
                        setScheduleRow(null);
                        setBreakdownRow(row);
                      }}
                      onItemClick={(row) => {
                        setBreakdownRow(null);
                        setScheduleRow(row);
                      }}
                    />
                  </>
                )}
              </Spin>
            )}
          </div>
        </Card>

        <PhaseCostDrawer
          open={phaseDrawerOpen}
          phases={basePhases}
          onClose={() => setPhaseDrawerOpen(false)}
          getContainer={popupContainer}
        />

        <ManpowerMonthlyScheduleDrawer
          open={!!scheduleRow}
          projectId={projectId}
          row={scheduleRow}
          phaseId={phaseId}
          activityId={activityId}
          onClose={() => setScheduleRow(null)}
          getContainer={popupContainer}
        />

        <ManpowerTaskBreakdownDrawer
          open={!!breakdownRow}
          projectId={projectId}
          row={breakdownRow}
          phaseId={phaseId}
          activityId={activityId}
          onClose={() => setBreakdownRow(null)}
          getContainer={popupContainer}
        />
        </ConfigProvider>
        </div>
      </Content>
    </>
  );
};

export default ManpowerCostAnalysisReport;
